import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import DashboardPage from "@/pages/Dashboard";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import { ReactNode } from "react";
import MainLayout from "@/components/layout/MainLayout";
import SearchResultsPage from "./components/layout/SearchResults";
import SettingsPage from "./pages/Settings";
import UsersPage from "./pages/Users";
import ClientsPage from "./pages/Clients";
import LoansPage from "./pages/Loans";
import LoanPlansPage from "./pages/LoanPlans";
import RevenuePage from "./pages/Revenue";
import DialogHost from "@/components/ui/dialog-host";
import { isLoggedIn as checkIsLoggedIn } from "@/utils/service";

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  if (!checkIsLoggedIn()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const PublicRoute = ({ children }: { children: ReactNode }) => {
  if (checkIsLoggedIn()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Wrapper component for pages that need MainLayout
const LayoutWrapper = ({ children }: { children: ReactNode }) => {
  return <MainLayout>{children}</MainLayout>;
};

function AppContent() {
  return (
    <Router>
      <DialogHost />
      <div className="min-h-screen bg-background">
        <Routes>
          {/* Public Routes - No Layout */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/set-password"
            element={
              <PublicRoute>
                <SetPassword />
              </PublicRoute>
            }
          />

          {/* Protected Routes - All wrapped in MainLayout */}
          {/* Dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DashboardPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SearchResultsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loans"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <LoansPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/loan-plans"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <LoanPlansPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/revenue"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <RevenuePage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <UsersPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Clients Routes */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SettingsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              checkIsLoggedIn() ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
        <Toaster position="bottom-right" closeButton richColors={false} />
      </div>
    </Router>
  );
}

function App() {
  return <AppContent />;
}

export default App;
